import { Anchor, Box, PinInput, Stack, Text } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { LogoWithIcon } from 'src/UI/app-container/header/Header';
import { Button, Link } from 'src/components';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';
import { AuthCard } from 'src/auth/components/AuthCard';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const codeSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits'),
});

type CodeFormValues = z.infer<typeof codeSchema>;

interface RecoveryCodeFormProps {
  email: string;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (code: string) => void;
  onBack: () => void;
}

export const RecoveryCodeForm = ({
  email,
  error,
  isSubmitting,
  onSubmit,
  onBack,
}: RecoveryCodeFormProps) => {
  const form = useForm<CodeFormValues>({
    mode: 'controlled',
    initialValues: {
      code: '',
    },
    validate: schemaResolver(codeSchema, { sync: true }),
    validateInputOnChange: true,
  });

  const handleSubmit = (values: CodeFormValues) => {
    onSubmit(values.code);
  };

  const isValidCode = form.values.code.length === 6;

  return (
    <AuthCard>
      <Stack gap="md">
        <Box mb="lg">
          <LogoWithIcon textColor="gray.10" />
        </Box>

        <Text fz="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
          Enter your code
        </Text>

        <Text fz="md" fw={FONT_WEIGHTS.medium} c="gray.10" mb="xs">
          We&apos;ve sent a 6-digit code to{' '}
          <Text span fw={FONT_WEIGHTS.semibold}>
            {email}
          </Text>
        </Text>

        <AuthFeedback error={error} />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md" align="center">
            <PinInput
              length={6}
              type="number"
              size="lg"
              autoFocus
              key={form.key('code')}
              {...form.getInputProps('code')}
              oneTimeCode
              aria-label="Recovery code"
            />

            <Button
              type="submit"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting || !isValidCode}
              variant="primary"
            >
              Verify code
            </Button>
          </Stack>
        </form>

        <Text ta="center" size="sm" c="gray.6">
          Didn&apos;t receive the code? Check your spam folder or{' '}
          <Anchor component="button" type="button" onClick={onBack} c="orange" size="sm">
            try again
          </Anchor>
        </Text>

        <Text ta="center" size="sm">
          <Link to="/login" c="orange" size="textSm">
            Back to login
          </Link>
        </Text>
      </Stack>
    </AuthCard>
  );
};
